import Cookies from 'js-cookie';

/**
 * How many rows fit on one page of a table. Absent until the user chooses, so the
 * page falls back to whatever default it asks for.
 */
export interface TablePreferences {
  perPage?: number;
}

/** Keyed by the page key passed to useTablePreferences. */
type PreferencesByPage = Record<string, TablePreferences>;

/**
 * Table preferences for every page, in a single cookie. Kept per page rather than
 * globally: a useful page size on Classes, with its 23 columns, is not the same as
 * on Languages, with three. Density is the opposite - it follows the user's screen,
 * not the table - and lives in densityPreference instead.
 *
 * One cookie rather than one per page keeps this well inside the 4KB budget and
 * means a single read on mount. Mirrors themePreference: a stopgap until there is
 * a real user-preferences screen, at which point only this module changes.
 */
const PREFS_COOKIE = 'cas_table_prefs';

const ONE_YEAR_IN_DAYS = 365;

const readAll = (): PreferencesByPage => {
  const raw = Cookies.get(PREFS_COOKIE);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    // A truncated or hand-edited cookie must not take every table in the app down
    // with it; falling back to the defaults is always safe.
    return parsed !== null && typeof parsed === 'object' ? parsed as PreferencesByPage : {};
  } catch {
    return {};
  }
};

export const readTablePreferences = (pageKey: string): TablePreferences => readAll()[pageKey] ?? {};

/** Merges into what is already stored for this page, leaving the other pages alone. */
export const saveTablePreferences = (pageKey: string, changes: TablePreferences): void => {
  const all = readAll();
  all[pageKey] = { ...all[pageKey], ...changes };

  Cookies.set(PREFS_COOKIE, JSON.stringify(all), {
    expires: ONE_YEAR_IN_DAYS,
    sameSite: 'strict',
    // Unlike the auth cookies this one is not sensitive, and hard-coding `secure`
    // would stop it persisting when the app is served over plain http in dev.
    secure: window.location.protocol === 'https:'
  });
};
